<template>
  <div>
    <v-text-field
      v-model="formData.kv"
      label="KV"
      outlined
      clearable
      dense
      autofocus
      @keydown.enter="fetchInvoice"
    ></v-text-field>

    <h3>Preview</h3>
    <p v-if="state.noData">No Data</p>

    <v-row
      class="fill-height"
      align-content="center"
      justify="center"
      v-if="state.loading"
    >
      <v-col class="text-subtitle-1 text-center" cols="12">
        Loading ...
      </v-col>
      <v-col cols="6">
        <v-progress-linear
          color="deep-purple accent-4"
          indeterminate
          rounded
          height="6"
        ></v-progress-linear>
      </v-col>
    </v-row>

    <div
      v-for="data in invoices"
      :key="data.id"
      class="report page-break"
      id="section-print"
    >
      <div class="header">
        <div class="left">
          <h3 class="report-title">NOTA PENAGIHAN BIAYA PENGIRIMAN BARANG</h3>
          <h4 class="text-center">{{ companyName }}</h4>
        </div>
        <div class="right ml-5">
          <table>
            <tr>
              <td>Nomor Nota</td>
              <td>:</td>
              <td class="border">{{ data.nomor_nota }}</td>
            </tr>
            <tr>
              <td>Kode Nota</td>
              <td>:</td>
              <td class="border-dotted">{{ data.kode_nota }}</td>
            </tr>
          </table>
        </div>
      </div>

      <div class="mt-4">
        <div class="invoice-info">
          <h4 class="mb-0 font-italic">
            Ditagih Kepada: {{ data.customer.nama }}
            <template v-if="data.customer.alamat"
              >, {{ data.customer.alamat }}</template
            >
          </h4>
          <p class="mb-0 font-italic" v-if="data.no_pelunasan">
            No Pelunasan: {{ data.no_pelunasan }}
          </p>
          <h3 class="mb-0 font-italic">
            Nama Kapal: {{ data.vessel.nama_kapal }}
          </h3>
          <p class="mb-0 font-italic">Voyage: {{ data.vessel.voy || "-" }}</p>
          <p class="mb-0 font-italic">KV: {{ data.vessel.kv || "-" }}</p>
          <p class="mb-0 font-italic" v-if="data.keterangan_barang">
            Keterangan Tagihan: {{ data.keterangan_barang }}
          </p>
        </div>

        <div class="items mt-3">
          <p class="m-0">Keterangan Pengiriman:</p>

          <table width="100%">
            <thead>
              <th>Kode TTB</th>
              <th>No TTB</th>
              <th>KC / NoCont</th>
              <th>Kondisi</th>
              <th>Dikirim Kepada</th>
              <th class="hide-on-print">Tonase</th>
              <th class="hide-on-print">Kubikasi</th>
              <th>Koli</th>
              <th>Nama Barang</th>
            </thead>
            <tbody>
              <tr v-for="item in data.invoice_detil" :key="item.id">
                <td class="text-center">{{ item.kodettb }}</td>
                <td class="text-center">{{ item.nottb }}</td>
                <td class="text-center">
                  {{ item.kc }} / {{ item.nomor_container }}
                </td>
                <td class="text-center">
                  {{ item.ttb.kondisi_detil.kondisi }}
                </td>
                <td class="text-center">
                  {{ item.ttb.penerima_detil.nama }}
                </td>
                <td class="text-center hide-on-print">
                  {{ total_tonase(item.ttb.ttb_rinci) }}
                </td>
                <td class="text-center hide-on-print">
                  {{ total_kubikasi(item.ttb.ttb_rinci) }}
                </td>
                <td class="text-center">
                  {{ item.koli_in || total_koli(item.ttb.ttb_rinci) }}
                </td>
                <td>
                  <ul>
                    <li v-for="detil in item.ttb.ttb_detil" :key="detil.id">
                      {{ detil.nama_barang }}
                    </li>
                  </ul>
                </td>
              </tr>
            </tbody>
          </table>

          <div class="price d-flex justify-center mt-5 mb-5">
            <table width="70%">
              <tbody>
                <tr>
                  <td>Freight Charge I:</td>
                  <td class="text-center">
                    {{ data.i_jumlah }} {{ data.i_satuan }}
                  </td>
                  <td>x</td>
                  <td class="text-center">
                    {{ formatCurrency(data.i_harga) }}
                  </td>
                  <td>=</td>
                  <td>{{ formatCurrency(data.i_total) }}</td>
                </tr>
                <tr>
                  <td>Freight Charge II:</td>
                  <td class="text-center">
                    {{ data.ii_jumlah }} {{ data.ii_satuan }}
                  </td>
                  <td>x</td>
                  <td class="text-center">
                    {{ formatCurrency(data.ii_harga) }}
                  </td>
                  <td>=</td>
                  <td>{{ formatCurrency(data.ii_total) }}</td>
                </tr>
                <tr>
                  <td colspan="4" class="text-center">{{ data.biaya_lain }}</td>
                  <td>=</td>
                  <td>{{ formatCurrency(data.biaya_lain_total) }}</td>
                </tr>
                <tr>
                  <td colspan="5"></td>
                  <td>
                    Rp.
                    {{
                      formatCurrency(
                        Math.abs(data.i_total) +
                          Math.abs(data.ii_total) +
                          Math.abs(data.biaya_lain_total)
                      )
                    }}
                  </td>
                </tr>
                <tr v-if="data.use_ppn">
                  <td colspan="4" class="text-center">
                    PPN ({{ data.ppn_percentage }}%)
                  </td>
                  <td>=</td>
                  <td>Rp. {{ formatCurrency(data.ppn_amount) }}</td>
                </tr>
                <tr v-if="data.use_pph">
                  <td colspan="4" class="text-center">
                    PPh ({{ data.pph_percentage }}%)
                  </td>
                  <td>=</td>
                  <td>Rp. -{{ formatCurrency(data.pph_amount) }}</td>
                </tr>
                <tr v-if="data.use_ppn || data.use_pph">
                  <td colspan="5" class="text-right">
                    <strong>Total Akhir:</strong>
                  </td>
                  <td>
                    <strong
                      >Rp.
                      {{
                        formatCurrency(
                          Math.abs(data.i_total) +
                            Math.abs(data.ii_total) +
                            Math.abs(data.biaya_lain_total) +
                            (data.use_ppn ? data.ppn_amount : 0) -
                            (data.use_pph ? data.pph_amount : 0)
                        )
                      }}</strong
                    >
                  </td>
                </tr>
                <tr>
                  <td colspan="6">{{ data.terbilang }}</td>
                </tr>
                <tr>
                  <td colspan="4" class="pt-5"></td>
                  <td colspan="2" class="pt-5">
                    {{ port_awal }},
                    {{ tgl_nota(data.tgl_nota) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Print Button -->
    <v-btn
      class="mx-2"
      fab
      dark
      right
      bottom
      fixed
      color="success"
      @click="printDocument"
    >
      Print
    </v-btn>
  </div>
</template>

<script>
import axios from "axios";
import variableMixin from "@/mixins/variableMixin";

export default {
  mixins: [variableMixin],
  name: "Invoice",
  data() {
    return {
      port_awal: JSON.parse(localStorage.getItem("port_awal")).text,
      state: {
        loading: false,
        noData: true
      },
      formData: {
        kv: ""
      },
      invoices: [],
      months: [
        "Januari",
        "Februari",
        "Maret",
        "April",
        "Mei",
        "Juni",
        "Juli",
        "Agustus",
        "September",
        "Oktober",
        "November",
        "Desember"
      ],
      days: ["Minggu", "Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu"]
    };
  },
  watch: {
    "formData.kv"(val) {
      if (val) {
        this.formData.kv = val.toUpperCase();
      }
    }
  },
  computed: {
    today() {
      let date = new Date();
      let day = date.getDay();
      let month = date.getMonth();
      let year = date.getFullYear();

      return `${this.days[day]}, ${date.getDate()} ${
        this.months[month]
      } ${year}`;
    }
  },
  methods: {
    printDocument() {
      window.print();
    },
    async fetchInvoice() {
      this.state.loading = true;

      await axios
        .get(`invoice/nota-p?kv=${this.formData.kv}`)
        .then(res => {
          // if (res.data.length == 0) {
          //   this.state.noData = true;
          // } else {
          // this.state.noData = false;
          // }

          this.invoices = res.data;
          this.state.loading = false;
        })
        .catch(err => {
          this.state.loading = false;

          alert(err.response.data.message);
        });
    },
    tgl_nota(data) {
      let date = new Date(data);
      let month = date.getMonth();
      let year = date.getFullYear();

      return `${date.getDate()} ${this.months[month]} ${year}`;
    },
    total_koli(data) {
      let total = 0;

      data.forEach(invoice => {
        total += parseInt(invoice.koli);
      });

      return total;
    },
    total_kubikasi(data) {
      let total = 0;

      data.forEach(invoice => {
        total += invoice.m3;
      });

      return total.toFixed(2);
    },
    total_tonase(data) {
      let total = 0;

      data.forEach(invoice => {
        total += invoice.ton;
      });

      return total.toFixed(2);
    },
    formatCurrency(num) {
      return new Intl.NumberFormat("id-ID").format(num);
    }
  }
};
</script>

<style type="text/css" media="print">
div.page-break {
  page-break-after: always !important;
  page-break-inside: avoid !important;
}
</style>

<style lang="css" scoped>
.report {
  background-color: white;
  width: 100%;
  font-family: times;
  padding: 20px;
  color: black;
}

.header {
  display: flex;
}

.header .left {
  border: 1px solid black;
}

.left .report-title {
  text-decoration: underline;
}

.right {
  font-style: italic;
  font-weight: bold;
  font-size: 1.1rem;
}

.right .border {
  text-align: center;
  padding: 0px 10px;
  border: 1px solid black;
}

.right .border-dotted {
  text-align: center;
  padding: 0px 10px;
  border: 1px solid black;
  border-style: dashed;
}

.hide-on-print {
  /* visible normally */
}

@media print {
  .hide-on-print {
    display: none !important;
  }
}
</style>
